import { supabase } from '../lib/supabase';

// All Supabase calls for the inspector flow go through this file — see
// CLAUDE.md ("All Supabase calls go through src/api/... only") and the
// matching convention in api/listings.js.

const BUCKET = 'inspection-photos';

// ─── Compression ───────────────────────────────────────────────────────────
// Runs at capture time, before anything leaves the device. Phone photos
// embed rotation as EXIF rather than rotating pixels, so decoding with
// createImageBitmap's imageOrientation:"from-image" is what makes the
// canvas draw come out upright without us reading EXIF ourselves.

const MAX_EDGE = 1600;
const QUALITY = 0.78;
const RETRY_QUALITY = 0.65;
const TARGET_BYTES = 250 * 1024; // ~250KB

export async function compressPhoto(file, opts = {}) {
  const maxEdge = opts.maxEdge ?? MAX_EDGE;
  const quality = opts.quality ?? QUALITY;
  const retryQuality = opts.retryQuality ?? RETRY_QUALITY;
  const targetBytes = opts.targetBytes ?? TARGET_BYTES;

  const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  try {
    const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
    const w = Math.max(1, Math.round(bitmap.width * scale));
    const h = Math.max(1, Math.round(bitmap.height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(bitmap, 0, 0, w, h);

    const toBlob = (q) => new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', q));

    let blob = await toBlob(quality);
    if (blob && blob.size > targetBytes) {
      const retried = await toBlob(retryQuality);
      if (retried && retried.size < blob.size) blob = retried;
    }
    if (!blob) throw new Error('Could not encode photo');
    return blob;
  } finally {
    bitmap.close?.();
  }
}

// ─── Upload ─────────────────────────────────────────────────────────────────
// bucket is public-read; path is {bookingId}/{random uuid}.jpg so a listing
// of the bucket (if one ever leaked) can't be walked sequentially.

function randomId() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export async function uploadInspectionPhoto(bookingId, blob) {
  try {
    const path = `${bookingId}/${randomId()}.jpg`;
    const { error: uploadError } = await supabase.storage.from(BUCKET).upload(path, blob, {
      contentType: 'image/jpeg',
      cacheControl: '31536000',
      upsert: false,
    });
    if (uploadError) return { url: null, error: uploadError };

    const { data } = supabase.storage.from(BUCKET).getPublicUrl(path);
    if (!data?.publicUrl) return { url: null, error: new Error('No public URL returned') };
    return { url: data.publicUrl, error: null };
  } catch (e) {
    return { url: null, error: e };
  }
}
